package com.rosario.juridico.marketplace.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.OfertaDataModel
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState
import com.rosario.juridico.navigation.Routes

private val VerdeAbogado = Color(0xFF1D9E75)
private val AzulCliente  = Color(0xFF1A7FD4)

@Composable
fun DetalleMisOfertasScreen(
    consultaId: String,
    navController: NavController,
    viewModel: MarketplaceViewModel,
) {
    val ofertasState by viewModel.ofertasRecibidasState.collectAsState()

    LaunchedEffect(consultaId) {
        viewModel.loadOfertasRecibidas(consultaId)
    }

    Column(modifier = Modifier.fillMaxSize()) {
        MktHeader(
            titulo = "Ofertas Recibidas",
            subtitulo = "Presupuestos de profesionales postulados",
            onBack = { navController.popBackStack() },
        )

        when (val state = ofertasState) {
            is UiState.Idle, is UiState.Loading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator(color = VerdeAbogado)
                }
            }
            is UiState.Success -> {
                if (state.data.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center,
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            Icon(
                                Icons.Default.Inbox,
                                null,
                                modifier = Modifier.size(48.dp),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            )
                            Text(
                                "Aún no has recibido ofertas para este caso",
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        items(state.data) { oferta ->
                            OfertaRecibidaCard(
                                oferta = oferta,
                                onVerPerfil = {
                                    navController.navigate("perfil_abogado/${oferta.abogadoUid}")
                                },
                                onContactar = {
                                    navController.navigate("chat/$consultaId")
                                },
                            )
                        }
                    }
                }
            }
            is UiState.Error -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = state.message,
                        color = MaterialTheme.colorScheme.error,
                    )
                }
            }
        }
    }
}

@Composable
fun OfertaRecibidaCard(
    oferta: OfertaDataModel,
    onVerPerfil: () -> Unit,
    onContactar: () -> Unit,
) {
    val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(2.dp),
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Icon(Icons.Default.Verified, null, tint = VerdeAbogado, modifier = Modifier.size(18.dp))
                    Text(
                        text = "Profesional Matriculado",
                        style = MaterialTheme.typography.titleSmall.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.onSurface,
                    )
                }

                if (oferta.montoPropuesto == 0.0) {
                    Surface(
                        shape = RoundedCornerShape(8.dp),
                        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                    ) {
                        Text(
                            text = "Honorarios a convenir",
                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                } else {
                    Surface(
                        shape = RoundedCornerShape(8.dp),
                        color = VerdeAbogado.copy(alpha = 0.12f),
                    ) {
                        Text(
                            text = "$${oferta.montoPropuesto}",
                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                            style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.ExtraBold),
                            color = VerdeAbogado,
                        )
                    }
                }
            }

            val ofertaMat = oferta.matricula.orEmpty()
            val ofertaTel = oferta.telefono.orEmpty()

            if (ofertaMat.isNotBlank() || ofertaTel.isNotBlank()) {
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    if (ofertaMat.isNotBlank()) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.Badge, null, modifier = Modifier.size(14.dp), tint = MaterialTheme.colorScheme.onSurfaceVariant)
                            Spacer(Modifier.width(6.dp))
                            Text("Matrícula: $ofertaMat", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        }
                    }
                    if (ofertaTel.isNotBlank()) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.Phone, null, modifier = Modifier.size(14.dp), tint = MaterialTheme.colorScheme.onSurfaceVariant)
                            Spacer(Modifier.width(6.dp))
                            Text("Teléfono: $ofertaTel", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        }
                    }
                }
            }

            if (oferta.mensaje.isNotBlank()) {
                Text(
                    text = oferta.mensaje,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                OutlinedButton(
                    onClick = onVerPerfil,
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(12.dp),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = VerdeAbogado),
                ) {
                    Icon(Icons.Default.AccountCircle, null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(4.dp))
                    Text("Ver Perfil", fontWeight = FontWeight.SemiBold, fontSize = 12.sp)
                }

                Button(
                    onClick = {
                        val telLimpio = oferta.telefono.orEmpty().filter { it.isDigit() }
                        try {
                            uriHandler.openUri("https://wa.me/549$telLimpio")
                        } catch (e: Exception) {
                            onContactar()
                        }
                    },
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(12.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = AzulCliente),
                ) {
                    Icon(Icons.Default.Chat, null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(4.dp))
                    Text("WhatsApp", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                }
            }
        }
    }
}
